import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, Stagger, itemVariant } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { getKnowledgeBySlug, getKnowledgeArticles } from "../data/knowledge";
import { COMPANY } from "../data/content";

const DOMAIN = "https://www.plasto-nord.com";

function DataTable({ table }) {
  return (
    <div className="mt-5 overflow-x-auto rounded-xl border border-[#e4e9f0]">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="bg-[#16181D] text-white">
            {table.head.map((h, i) => (
              <th key={i} className="px-4 py-3 font-head font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 ? "bg-[#f6f8fb]" : "bg-white"}>
              {row.map((cell, ci) => (
                <td key={ci} className={`px-4 py-3 align-top text-[#4a4d52] ${ci === 0 ? "font-medium text-[#16181D]" : ""}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function KnowledgeArticle() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const bySlug = getKnowledgeBySlug(lang);
  const a = bySlug[slug];
  if (!a) return <Navigate to="/wissen" replace />;

  const crumbs = [{ name: t("kn.home"), path: "/" }, { name: t("nav.knowledge"), path: "/wissen" }, { name: a.title, path: `/wissen/${a.slug}` }];
  const related = (a.related || []).map((s) => bySlug[s]).filter(Boolean);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: a.title,
    description: a.metaDescription,
    image: a.hero,
    inLanguage: lang === "en" ? "en" : "de-DE",
    author: { "@type": "Organization", name: COMPANY.name },
    publisher: { "@id": DOMAIN + "/#organization" },
    mainEntityOfPage: { "@type": "WebPage", "@id": DOMAIN + `/wissen/${a.slug}` },
    about: lang === "en" ? "Thermoplastic polyurethane (TPU)" : "Thermoplastisches Polyurethan (TPU)",
    keywords: (a.keywords || []).join(", "),
  };
  const faqSchema = a.faq && a.faq.length ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: a.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  } : null;

  return (
    <article data-testid="knowledge-article">
      <Seo
        title={a.metaTitle}
        description={a.metaDescription}
        path={`/wissen/${a.slug}`}
        image={a.hero}
        breadcrumbs={crumbs}
        schema={faqSchema ? [articleSchema, faqSchema] : [articleSchema]}
      />

      {/* Hero */}
      <section className="border-b border-[#e4e9f0] bg-[#f6f8fb] grain">
        <div className="pn-container py-12 md:py-16">
          <Breadcrumbs items={crumbs} />
          <Reveal>
            <p className="overline">{t("nav.knowledge")} · {a.date}</p>
            <h1 className="mt-4 max-w-4xl font-head text-3xl font-light leading-[1.1] tracking-tight text-[#16181D] md:text-5xl">{a.title}</h1>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={a.hero} alt={a.title + " – PlastoNord"} className="mt-8 aspect-[16/8] w-full rounded-2xl border border-[#e4e9f0] object-cover shadow-xl" loading="eager" width="1264" height="632" />
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="py-14 md:py-20">
        <div className="pn-container grid items-start gap-12 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0">
            {/* Kurzfassung / TL;DR for AI + readers */}
            <Reveal>
              <div className="rounded-2xl border border-[#E2611C]/30 bg-[#FBEADD]/40 p-6" data-testid="article-summary">
                <p className="overline flex items-center gap-2"><Sparkles size={14} className="text-[#E2611C]" /> {t("kn.summary")}</p>
                <p className="mt-3 leading-relaxed text-[#16181D]">{a.summary}</p>
              </div>
            </Reveal>

            {/* Auf einen Blick */}
            {a.takeaways?.length > 0 && (
              <Reveal delay={0.05}>
                <div className="mt-8">
                  <h2 className="font-head text-2xl font-semibold text-[#16181D]">{t("kn.glance")}</h2>
                  <ul className="mt-4 space-y-2.5">
                    {a.takeaways.map((it, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[#4a4d52]"><Check size={18} className="mt-0.5 shrink-0 text-[#E2611C]" /> {it}</li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}

            {/* Sections */}
            <div className="mt-12 space-y-12">
              {a.sections.map((s, i) => (
                <Reveal key={i} delay={0.03 * i}>
                  <div>
                    <h2 className="font-head text-2xl font-semibold tracking-tight text-[#16181D] md:text-3xl">{s.h}</h2>
                    {s.p?.map((para, pi) => (
                      <p key={pi} className="mt-4 leading-relaxed text-[#4a4d52]">{para}</p>
                    ))}
                    {s.list && (
                      <ul className="mt-4 space-y-2.5">
                        {s.list.map((li, lii) => (
                          <li key={lii} className="flex items-start gap-2.5 text-[#4a4d52]"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E2611C]" /> {li}</li>
                        ))}
                      </ul>
                    )}
                    {s.table && <DataTable table={s.table} />}
                    {s.note && (
                      <p className="mt-4 rounded-lg border-l-2 border-[#E2611C] bg-[#f6f8fb] px-4 py-3 text-sm text-[#4a4d52]"><strong className="text-[#16181D]">{t("kn.note")}</strong> {s.note}</p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>

            {/* FAQ */}
            {a.faq?.length > 0 && (
              <Reveal>
                <div className="mt-14" data-testid="article-faq">
                  <h2 className="font-head text-2xl font-semibold tracking-tight text-[#16181D] md:text-3xl">{t("kn.faq")}</h2>
                  <Accordion type="single" collapsible className="mt-6">
                    {a.faq.map((f, i) => (
                      <AccordionItem key={i} value={`q${i}`} data-testid={`article-faq-${i}`} className="border-b border-[#e4e9f0]">
                        <AccordionTrigger className="text-left font-head text-base font-medium text-[#16181D] hover:text-[#E2611C] hover:no-underline">{f.q}</AccordionTrigger>
                        <AccordionContent className="leading-relaxed text-[#4a4d52]">{f.a}</AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </Reveal>
            )}

            {/* CTA */}
            <Reveal>
              <div className="mt-14 flex flex-wrap gap-3 rounded-2xl bg-[#16181D] p-8">
                <div className="flex-1 min-w-[240px]">
                  <h2 className="font-head text-xl font-semibold text-white">{t("kn.advice")} {a.title.split(":")[0]}?</h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/70">{t("kn.adviceText")} – {COMPANY.hours}.</p>
                </div>
                <Link to="/kontakt" className="btn-primary self-center" data-testid="article-contact-cta">{t("kn.send")} <ArrowRight size={16} /></Link>
              </div>
            </Reveal>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24">
            {a.relatedProducts?.length > 0 && (
              <div className="pn-card p-6" data-testid="article-related-products">
                <p className="overline">{t("kn.relatedProducts")}</p>
                <ul className="mt-4 space-y-2">
                  {a.relatedProducts.map((p, i) => (
                    <li key={i}>
                      <Link to={p.href} className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-[#16181D] transition-colors hover:bg-[#f6f8fb] hover:text-[#E2611C]">
                        {p.label} <ArrowRight size={15} className="text-[#E2611C]" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {related.length > 0 && (
              <div className="pn-card p-6" data-testid="article-related-articles">
                <p className="overline">{t("kn.relatedArticles")}</p>
                <ul className="mt-4 space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/wissen/${r.slug}`} className="group flex items-start gap-2 text-sm font-medium text-[#16181D] hover:text-[#E2611C]">
                        <ArrowUpRight size={15} className="mt-0.5 shrink-0 text-[#E2611C]" /> {r.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* More from knowledge */}
      <section className="bg-[#f6f8fb] py-16">
        <div className="pn-container">
          <h2 className="font-head text-2xl font-medium text-[#16181D]">{t("kn.moreFromHub")}</h2>
          <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {getKnowledgeArticles(lang).filter((x) => x.slug !== a.slug).slice(0, 6).map((x) => (
              <motion.div key={x.slug} variants={itemVariant}>
                <Link to={`/wissen/${x.slug}`} className="pn-card flex h-full items-center justify-between gap-3 p-5" data-testid={`knowledge-more-${x.slug}`}>
                  <span className="font-head text-sm font-medium text-[#16181D]">{x.title}</span>
                  <ArrowRight size={16} className="shrink-0 text-[#E2611C]" />
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
    </article>
  );
}
